import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  User, Target, Video, ClipboardList, MessageSquare, ArrowLeft,
  Calendar, TrendingUp, Award, CheckCircle, Plus, Save, X, Lock, Info
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AthleteWorkspace = () => {
  const { athleteId } = useParams();
  const { user, getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Notes state
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [submittingNote, setSubmittingNote] = useState(false);
  
  // Video comment state
  const [videoComment, setVideoComment] = useState('');

  // Check if this is an invited coach (limited access)
  const isInvitedCoach = user?.coach_mode === 'invited_only';

  useEffect(() => {
    fetchData();
  }, [athleteId]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/invited-athlete/${athleteId}`, { headers: getAuthHeaders() });
      setData(res.data);
      
      // Fetch notes
      try {
        const notesRes = await axios.get(`${API}/invited-athlete/${athleteId}/notes`, { headers: getAuthHeaders() });
        setNotes(notesRes.data);
      } catch (e) {
        // Notes might not exist yet
      }
    } catch (error) {
      console.error('Failed to fetch athlete:', error);
      toast.error(error.response?.data?.detail || 'Failed to load athlete');
    }
    setLoading(false);
  };

  // === NOTES HANDLERS (Allowed for invited coaches) ===
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setSubmittingNote(true);
    try {
      await axios.post(`${API}/invited-athlete/${athleteId}/notes`, {
        content: newNote,
        category: 'feedback'
      }, { headers: getAuthHeaders() });
      toast.success('Feedback added');
      setNewNote('');
      // Refresh notes
      const notesRes = await axios.get(`${API}/invited-athlete/${athleteId}/notes`, { headers: getAuthHeaders() });
      setNotes(notesRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add note');
    }
    setSubmittingNote(false);
  };

  // === VIDEO COMMENT (Allowed for invited coaches) ===
  const handleAddVideoComment = async (videoId) => {
    if (!videoComment.trim()) return;
    try {
      await axios.post(`${API}/invited-athlete/${athleteId}/video-comment`, {
        video_id: videoId,
        comment: videoComment
      }, { headers: getAuthHeaders() });
      toast.success('Comment added');
      setVideoComment('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add comment');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Unable to access this athlete's data</p>
        <Link to="/coach/invited-athletes">
          <Button variant="outline" className="mt-4">
            <ArrowLeft size={16} className="mr-2" />
            Back to My Athletes
          </Button>
        </Link>
      </div>
    );
  }

  const { athlete, primary_coach, training_plan, sessions, technical_development, videos } = data;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'training', label: 'Training Plan', icon: ClipboardList },
    { id: 'videos', label: 'Video Analysis', icon: Video },
    { id: 'development', label: 'Technical Dev', icon: Target },
    { id: 'notes', label: 'Notes & Feedback', icon: MessageSquare },
  ];

  const completedSessions = sessions?.filter(s => s.status === 'completed').length || 0;
  const skills = ['ball_control', 'passing_receiving', 'dribbling', 'shooting', 'decision_making'];

  // Restricted Action Component
  const RestrictedAction = ({ label }) => (
    <div className="flex items-center gap-2 text-muted-foreground text-sm p-3 bg-muted/30 rounded-sm border border-dashed border-border">
      <Lock size={14} />
      <span>{label}</span>
      <div className="ml-auto group relative">
        <Info size={14} className="cursor-help" />
        <div className="absolute right-0 bottom-full mb-2 w-48 p-2 bg-card border border-border rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          Upgrade to a coach subscription to unlock this feature
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border/40 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center gap-4">
            <Link to="/coach/invited-athletes">
              <Button variant="ghost" size="sm">
                <ArrowLeft size={18} />
              </Button>
            </Link>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="text-primary font-bold text-lg">{athlete?.name?.[0]}</span>
                </div>
                <div>
                  <h1 className="font-heading text-xl md:text-2xl uppercase">{athlete?.name}</h1>
                  <p className="text-xs text-muted-foreground">
                    {athlete?.age} years • {athlete?.sport} {athlete?.position && `• ${athlete.position}`}
                  </p>
                </div>
              </div>
            </div>
            <div className="text-right hidden md:block">
              <p className="text-xs text-muted-foreground">Primary Coach</p>
              <p className="text-sm font-medium">{primary_coach?.name || 'Not assigned'}</p>
            </div>
          </div>

          {/* Access Badge - Invited Coach Limited */}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className="px-3 py-1 bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs rounded-full flex items-center gap-1">
              <User size={12} />
              Athlete-Invited Coach
            </span>
            <span className="text-xs text-muted-foreground">
              Limited access — upgrade to manage athletes
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex gap-1 overflow-x-auto pb-2 -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6">
        
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-card border border-border/40 p-4 rounded-sm">
                <Calendar className="text-primary mb-2" size={20} />
                <p className="text-2xl font-heading">{sessions?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Total Sessions</p>
              </div>
              <div className="bg-card border border-border/40 p-4 rounded-sm">
                <CheckCircle className="text-green-400 mb-2" size={20} />
                <p className="text-2xl font-heading text-green-400">{completedSessions}</p>
                <p className="text-xs text-muted-foreground">Completed</p>
              </div>
              <div className="bg-card border border-border/40 p-4 rounded-sm">
                <Video className="text-blue-400 mb-2" size={20} />
                <p className="text-2xl font-heading">{videos?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Videos</p>
              </div>
              <div className="bg-card border border-border/40 p-4 rounded-sm">
                <Award className="text-primary mb-2" size={20} />
                <p className="text-2xl font-heading">{training_plan ? 1 : 0}</p>
                <p className="text-xs text-muted-foreground">Active Plans</p>
              </div>
            </div>

            {/* Limited Access Notice */}
            <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-sm">
              <div className="flex items-start gap-3">
                <Lock className="text-amber-400 flex-shrink-0 mt-0.5" size={18} />
                <div>
                  <p className="font-medium text-amber-400 mb-1">Limited Access Mode</p>
                  <p className="text-sm text-muted-foreground mb-3">
                    As an athlete-invited coach, you can view the athlete's history and add feedback. 
                    To create training plans and manage sessions, upgrade to a coach subscription.
                  </p>
                  <Link to="/#pricing">
                    <Button size="sm" variant="outline" className="border-amber-400 text-amber-400 hover:bg-amber-400/10">
                      View Coach Plans
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            {/* What you CAN do */}
            <div className="bg-card border border-border/40 p-6 rounded-sm">
              <h3 className="font-heading text-lg uppercase mb-4">Available Actions</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-green-500/10 border border-green-500/30 rounded-sm">
                  <CheckCircle className="text-green-400" size={18} />
                  <span className="text-sm">View full training history</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-500/10 border border-green-500/30 rounded-sm">
                  <CheckCircle className="text-green-400" size={18} />
                  <span className="text-sm">View training plans</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-500/10 border border-green-500/30 rounded-sm">
                  <CheckCircle className="text-green-400" size={18} />
                  <span className="text-sm">Add notes and feedback</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-500/10 border border-green-500/30 rounded-sm">
                  <CheckCircle className="text-green-400" size={18} />
                  <span className="text-sm">Comment on video analysis</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Training Plan Tab - VIEW ONLY */}
        {activeTab === 'training' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="font-heading text-xl uppercase">Training Plan</h2>
              <RestrictedAction label="Create/Edit plans requires subscription" />
            </div>

            {training_plan ? (
              <div className="bg-card border border-border/40 p-6 rounded-sm">
                <h3 className="font-heading text-xl mb-2">{training_plan.name}</h3>
                <p className="text-muted-foreground mb-4">{training_plan.notes}</p>
                {training_plan.objectives?.length > 0 && (
                  <div>
                    <h4 className="text-sm uppercase text-muted-foreground mb-2">Objectives</h4>
                    <ul className="space-y-2">
                      {training_plan.objectives.map((obj, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className="text-primary mt-0.5" size={16} />
                          <span>{obj}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 bg-card border border-border/40 rounded-sm">
                <ClipboardList className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No training plan created yet</p>
                <p className="text-xs text-muted-foreground mt-1">The primary coach can create a training plan</p>
              </div>
            )}

            {/* Sessions - VIEW ONLY */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-heading text-lg uppercase">Sessions</h3>
                <RestrictedAction label="Schedule sessions requires subscription" />
              </div>

              {sessions?.length > 0 ? (
                <div className="space-y-3">
                  {sessions.map((session, idx) => (
                    <div key={idx} className="bg-card border border-border/40 p-4 rounded-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{new Date(session.date).toLocaleString()}</p>
                          <p className="text-sm text-muted-foreground">{session.focus_points || 'General training'}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded ${
                          session.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          session.status === 'scheduled' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {session.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-muted/30 rounded-sm">
                  <p className="text-muted-foreground">No sessions recorded yet</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Videos Tab - VIEW + COMMENT */}
        {activeTab === 'videos' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="font-heading text-xl uppercase">Video Analysis</h2>
              <RestrictedAction label="Upload videos requires subscription" />
            </div>

            {videos?.length > 0 ? (
              <div className="space-y-4">
                {videos.map((video, idx) => (
                  <div key={idx} className="bg-card border border-border/40 p-4 rounded-sm">
                    <div className="flex items-start gap-4">
                      <div className="w-24 h-16 bg-muted rounded flex items-center justify-center flex-shrink-0">
                        <Video className="text-muted-foreground" size={24} />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{video.title || 'Video Analysis'}</p>
                        <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">{video.description}</p>
                        {video.analysis_notes && (
                          <p className="text-sm mt-2 p-2 bg-muted/30 rounded whitespace-pre-line leading-relaxed">{video.analysis_notes}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">
                          {new Date(video.created_at).toLocaleDateString()}
                        </p>
                        
                        {/* Comment section for invited coaches */}
                        <div className="mt-3 pt-3 border-t border-border/40">
                          <p className="text-xs text-muted-foreground uppercase mb-2">Add your feedback</p>
                          <div className="flex gap-2">
                            <Textarea
                              placeholder="Add a comment on this video..."
                              value={videoComment}
                              onChange={(e) => setVideoComment(e.target.value)}
                              rows={2}
                              className="flex-1"
                            />
                            <Button 
                              size="sm" 
                              onClick={() => handleAddVideoComment(video.id)}
                              disabled={!videoComment.trim()}
                            >
                              Comment
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-card border border-border/40 rounded-sm">
                <Video className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No videos uploaded yet</p>
              </div>
            )}
          </div>
        )}

        {/* Technical Development Tab - VIEW ONLY */}
        {activeTab === 'development' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="font-heading text-xl uppercase">Technical Development</h2>
              <RestrictedAction label="Update skills requires subscription" />
            </div>

            <div className="bg-card border border-border/40 p-6 rounded-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {skills.map((skill) => {
                  const skillData = technical_development?.[skill];
                  return (
                    <div key={skill} className="p-4 bg-muted/30 rounded-sm">
                      <p className="text-sm text-muted-foreground uppercase mb-3">
                        {skill.replace(/_/g, ' ')}
                      </p>
                      <span className={`px-3 py-1 text-sm rounded ${
                        skillData?.status === 'Strong' ? 'bg-green-500/10 text-green-400' :
                        skillData?.status === 'Improving' ? 'bg-blue-500/10 text-blue-400' :
                        'bg-yellow-500/10 text-yellow-400'
                      }`}>
                        {skillData?.status || 'Not assessed'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Notes Tab - CAN ADD */}
        {activeTab === 'notes' && (
          <div className="space-y-6">
            <div className="bg-card border border-green-500/30 p-6 rounded-sm">
              <h3 className="font-heading text-lg uppercase mb-4 flex items-center gap-2">
                <CheckCircle className="text-green-400" size={18} />
                Add Your Feedback
              </h3>
              <Textarea
                placeholder="Share your observations, feedback, or notes about this athlete's development..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                className="mb-4"
                rows={4}
              />
              <Button 
                onClick={handleAddNote} 
                disabled={submittingNote || !newNote.trim()}
                className="bg-primary text-black"
              >
                {submittingNote ? 'Adding...' : 'Add Feedback'}
              </Button>
            </div>

            {notes.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-heading text-lg uppercase">Previous Notes</h3>
                {notes.map((note, idx) => (
                  <div key={idx} className="bg-card border border-border/40 p-4 rounded-sm">
                    <p className="text-sm">{note.content}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {new Date(note.created_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default AthleteWorkspace;
